import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  useFitment,
  useMyStore,
  useOptimisticData,
  usePrevious,
  useProductDetail
} from '../../../../shared/hooks/hooks';

import {
  getNormalizedProductType,
  getPrioritizedArticleStatusProduct
} from '../../../../shared/utils/product/product';

import { ARTICLE_STATUSES } from '../../../../shared/constants/products';

interface PageParams {
  packageCode?: string;
  productCode?: string;
  productSlug?: string;
  productType?: string;
  rearProductCode?: string;
}

interface LocationState {
  initialProductCodes?: string[];
}

export interface QueryFiltersState {
  name?: string;
  value?: string;
}

function usePDPProduct(treadwellPreferences, isTreadwellFlow) {
  const [isLoading, setIsLoading] = useState(true);
  const [isCanonicalWithoutFilterOptions, setIsCanonicalWithoutFilterOptions] =
    useState(false);
  const [error, setError] = useState<string>();
  const [isNoLongerAvailable, setIsNoLongerAvailable] = useState(false);
  const [queryFilters, setQueryFilters] = useState<QueryFiltersState[] | null>(
    null
  );
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(true);
  const [shouldFetchAllCanonicalData, setShouldFetchAllCanonicalData] =
    useState(false);
  const urlParams = useParams<PageParams>();
  const optimisticData = useOptimisticData();
  const { myStore } = useMyStore();
  const { isFitmentLoading, selectedFitment } = useFitment();
  const prevSelectedFitment = usePrevious(selectedFitment, true);
  const { state, pathname } = useLocation<LocationState>();
  const prevPathname = usePrevious(pathname, true);
  const isInitializedRef = useRef(false);

  const { packageCode, productCode, rearProductCode, productSlug } = urlParams;
  const isStaggered = Boolean(rearProductCode);
  const isSetSearch = Boolean(pathname?.includes('set'));
  const isPrebuiltPackage = Boolean(packageCode);
  const isCanonicalSearch = !productCode && !packageCode;

  const [product, setProduct] = useState(() => {
    let defaultValue;

    if (isPrebuiltPackage) {
      defaultValue = optimisticData.read('prebuiltPackage') || null;
    } else {
      defaultValue = optimisticData.read('product') || null;
    }

    return defaultValue;
  });

  const [weatherDetails, setWeatherDetails] = useState(null);

  const storeCode = window.isPreRendering ? '1344' : myStore?.code;
  const initialProductCodes = state?.initialProductCodes;
  const { getProductDetail } = useProductDetail(myStore);

  const shouldFetchProduct =
    storeCode && !isFitmentLoading && (isRefreshingData || shouldFetchData);

  const getProduct = useCallback(async () => {
    if (!myStore?.code) {
      return;
    }

    let queryType;

    try {
      setIsLoading(true);
      setShouldFetchData(false);

      const hasFilters = Boolean(queryFilters);
      const {
        product,
        recommendations,
        errors,
        queryType: queryTypeResult
      } = await getProductDetail({
        canonicalFilters: queryFilters,
        canonicalProductCodes: initialProductCodes || [],
        canonicalProductType: getNormalizedProductType(urlParams?.productType),
        fitment: selectedFitment,
        isTreadwellFlow,
        packageCode,
        productCode,
        productSlug,
        rearProductCode,
        shouldFetchAllCanonicalData,
        store: myStore,
        treadwellPreferences
      });

      queryType = queryTypeResult;

      const articleStatusProduct = isPrebuiltPackage
        ? getPrioritizedArticleStatusProduct(
            product?.product1,
            product?.product2
          )
        : product;
      const isNoLongerAvailable = Boolean(
        articleStatusProduct?.articleStatus === ARTICLE_STATUSES.OBSOLETE ||
          articleStatusProduct?.articleStatus === ARTICLE_STATUSES.DISCONTINUED
      );
      const newProduct = product || null;
      const newWeatherDetails = recommendations?.weatherDetails || null;

      if (
        Array.isArray(errors) ||
        (isCanonicalSearch && shouldFetchAllCanonicalData && !newProduct) ||
        (!isCanonicalSearch && !newProduct)
      ) {
        throw errors?.[0] || 'no product';
      }

      setIsCanonicalWithoutFilterOptions(
        Boolean(
          isCanonicalSearch &&
            newProduct &&
            newProduct.productType !== 'aggregate' &&
            !hasFilters
        )
      );

      setProduct(newProduct);
      setWeatherDetails(newWeatherDetails);
      setIsNoLongerAvailable(isNoLongerAvailable || false);
    } catch (err) {
      setError(
        `No ${queryType} product found | ${(err as Error)?.message || err}`
      );
    } finally {
      setIsLoading(false);
      setIsRefreshingData(false);
    }
  }, [
    myStore,
    queryFilters,
    getProductDetail,
    selectedFitment,
    isTreadwellFlow,
    treadwellPreferences,
    productCode,
    packageCode,
    rearProductCode,
    productSlug,
    urlParams,
    initialProductCodes,
    shouldFetchAllCanonicalData,
    isPrebuiltPackage,
    isCanonicalSearch
  ]);

  useEffect(() => {
    function resetData() {
      setQueryFilters(null);
      setProduct(null);
      setIsLoading(true);
      setIsRefreshingData(true);
      setShouldFetchAllCanonicalData(false);
    }

    if (
      isInitializedRef.current &&
      (prevSelectedFitment?.vehicleAssemblyId !==
        selectedFitment?.vehicleAssemblyId ||
        prevPathname !== pathname)
    ) {
      resetData();
    }
  }, [selectedFitment, prevSelectedFitment, pathname, prevPathname]);

  useEffect(() => {
    if (queryFilters || urlParams) {
      setShouldFetchData(true);
    }
  }, [queryFilters, urlParams]);

  useEffect(() => {
    // receiving null for canonical product when a vehicle is selected means
    // none of the products in the aggregate fit the vehicle so we instead
    // refetch all of the canonical data regardless of fit
    if (isCanonicalSearch && !isLoading && !product) {
      setQueryFilters(null);
      setShouldFetchAllCanonicalData(true);
      setShouldFetchData(true);
    }
  }, [isCanonicalSearch, product, isLoading]);

  useEffect(() => {
    if (shouldFetchProduct) {
      getProduct();
    }
  }, [shouldFetchProduct, getProduct]);

  return {
    error,
    isCanonicalSearch,
    isCanonicalWithoutFilterOptions,
    isLoading,
    isNoLongerAvailable,
    isPrebuiltPackage,
    isRefreshingData,
    isSetSearch,
    isStaggered,
    product,
    queryFilters,
    setQueryFilters,
    weatherDetails
  };
}

export default usePDPProduct;
